import { useEffect, useMemo, useState, FormEvent } from 'react';
import { useToast } from '@sectepe/ui';
import api, {
  EnterpriseTarget,
  FederationStatus,
  BulkResult,
  bulkOperation,
  newBulkOperationId,
  FederationAuthEvent,
  FederationAuthLogResponse,
  FederationAuthLogTenantCard,
  EnterpriseTargetHistoryEvent,
  EnterpriseTargetHistoryResponse,
  computeHistoryStats,
} from '../services/api';
import { BulkOperationsModal } from '../components/BulkOperationsModal';
import { useBrand } from '../contexts/BrandContext';
import { TypedConfirmModal } from '../components/TypedConfirmModal';
// Welle 4.6 QoL-6: shared German status labels.
import { FEDERATION_STATUS_LABEL as STATUS_LABELS } from '../services/status-labels';

const STATUS_COLORS: Record<FederationStatus, string> = {
  reachable: 'bg-green-100 text-green-800',
  unreachable: 'bg-amber-100 text-amber-800',
  auth_failed: 'bg-red-100 text-red-700',
  timeout: 'bg-amber-100 text-amber-800',
  disabled: 'bg-slate-200 text-slate-600',
  unknown: 'bg-slate-100 text-slate-500',
  local: 'bg-sky-100 text-sky-700',
};


// SYNC WITH shared/multi-tenant/src/reserved-slugs.ts (Welle 4.6 TD-4).
// The SPA is bundled with vite (ESM) and the shared package is CJS-only,
// so we don't `import` it here — the operator catches any drift by
// running the platform-admin route validation, which IS wired into the
// shared module. This regex+set are kept identical for client-side
// form validation only. `default` ist der Slug der zentralen Dienste.
const SLUG_RE = /^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/;
const RESERVED = new Set([
  'platform', 'master', 'admin', 'system', 'internal', 'default', 'access', 'ai',
  'ausschreibungsmatcher', 'auth', 'automation', 'awx', 'backup', 'booking', 'bus', 'chat',
  'chat-security', 'collabora', 'core', 'crm', 'cti', 'cti-compliance', 'cti-edge', 'customers', 'defectdojo',
  'dependency-track', 'desktop', 'device-enrollment', 'dfir', 'dms', 'dns', 'documenso',
  'document-hub', 'dovecot', 'edr', 'facilitator', 'federation', 'forms', 'ghcr', 'headscale',
  'hr-tool', 'iga', 'intermal', 'ionos', 'issue-tracker', 'itops-dashboard', 'itsm',
  'itsm-ai-agent', 'jitsi', 'kimai', 'lms', 'm365-migration', 'mail', 'mail-client',
  'mail-security', 'mailarchive', 'marketplace', 'mcp-comm', 'mcp-gateway', 'mdm', 'mesh',
  'midpoint', 'monitoring', 'networking', 'nextcloud', 'notes', 'notification-hub', 'office',
  'openbao', 'phishing', 'pki', 'platform-admin', 'platform-admin-tenants', 'platform-api', 'pm',
  'pm-tool', 'postiz', 'print', 'prod', 'production', 're', 'rooms', 'search', 'sectepe-agent',
  'sectepe-wiki', 'siem', 'soc-dashboard', 'spiderfoot', 'staging', 'stalwart', 'storage',
  'synapse', 'telephony', 'tenants', 'thehive', 'tier2', 'user-copilot', 'vault', 'vdi', 'video',
  'vpn', 'wazuh-ingest', 'web-proxy', 'webfilter', 'webhosting', 'wiki', 'zammad', 'zot',
]);

// Federation removes nuke the registry row + break central reads
// for that tenant. Even 1-2 unintended DELETEs is a real outage,
// so any bulk remove gets the typed-confirmation gate — the
// threshold mirrors the tenant-list "destructive op" cutoff and
// is kept at the slightly looser >5 for symmetry, but the
// confirmation phrase is REMOVE so it can't be muscle-memoried
// past a SUSPEND dialog.
const TYPED_CONFIRM_THRESHOLD = 5;

type BulkAction = 'enable' | 'disable' | 'remove';

interface RunState {
  action: BulkAction;
  title: string;
  rows: BulkResult<unknown>[];
  running: boolean;
}

function relativeAge(iso: string | null): string {
  if (!iso) return '—';
  const delta = Date.now() - new Date(iso).getTime();
  if (delta < 0) return 'in the future';
  const m = Math.floor(delta / 60_000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}

export function FederationTargetsPage() {
  const toast = useToast();
  const [rows, setRows] = useState<EnterpriseTarget[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [busySlug, setBusySlug] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [run, setRun] = useState<RunState | null>(null);
  // Welle 4.6 TD-6 — currently-open per-target history modal.
  // We carry the full EnterpriseTarget row (not just the slug)
  // because the modal header renders the current status badge
  // and last-reachable age, which already live on the table row.
  // Cloning vs re-fetching keeps the modal cheap to open.
  const [historyTarget, setHistoryTarget] = useState<EnterpriseTarget | null>(null);

  function refresh(): void {
    setLoading(true);
    api.get<EnterpriseTarget[]>('/enterprise-targets')
      .then((r) => setRows(r.data))
      .catch((err) => setError(err.response?.data?.error ?? err.message))
      .finally(() => setLoading(false));
  }

  useEffect(() => {
    refresh();
  }, []);

  async function toggleEnabled(t: EnterpriseTarget): Promise<void> {
    setBusySlug(t.slug);
    setError(null);
    try {
      const path = t.enabled
        ? `/enterprise-targets/${t.slug}/disable`
        : `/enterprise-targets/${t.slug}/enable`;
      await api.post(path);
      refresh();
    } catch (err) {
      // axios is the only HTTP client in the SPA; the response
      // shape is its standard error envelope. Falling back to
      // the JS Error message covers network-level failures.
      setError((err as { response?: { data?: { error?: string } }; message: string })
        .response?.data?.error ?? (err as Error).message);
    } finally {
      setBusySlug(null);
    }
  }

  async function deleteTarget(t: EnterpriseTarget): Promise<void> {
    // Defensive confirm — DELETE removes the row + breaks
    // federation for that tenant. Operators occasionally need
    // it (decommission, rotation), but never accidentally.
    if (!window.confirm(`Federation-Eintrag fuer ${t.slug} entfernen?`)) return;
    setBusySlug(t.slug);
    setError(null);
    try {
      await api.delete(`/enterprise-targets/${t.slug}`);
      toast.push({ message: `Federation-Eintrag ${t.slug} entfernt.`, severity: 'success' });
      refresh();
    } catch (err) {
      setError((err as { response?: { data?: { error?: string } }; message: string })
        .response?.data?.error ?? (err as Error).message);
    } finally {
      setBusySlug(null);
    }
  }

  const allSelected = rows.length > 0 && rows.every((r) => selected.has(r.slug));
  const selectedSlugs = useMemo(
    () => rows.filter((r) => selected.has(r.slug)).map((r) => r.slug),
    [rows, selected],
  );

  function toggleRow(slug: string): void {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(slug)) next.delete(slug);
      else next.add(slug);
      return next;
    });
  }

  function toggleAll(): void {
    setSelected((prev) => {
      if (rows.every((r) => prev.has(r.slug))) {
        const next = new Set(prev);
        rows.forEach((r) => next.delete(r.slug));
        return next;
      }
      const next = new Set(prev);
      rows.forEach((r) => next.add(r.slug));
      return next;
    });
  }

  async function runBulk<TResult>(
    action: BulkAction,
    title: string,
    targetSlugs: string[],
    op: (slug: string, headers: Record<string, string>) => Promise<TResult>,
  ): Promise<void> {
    if (targetSlugs.length === 0) return;
    const bulkId = newBulkOperationId();
    const headers = { 'X-Bulk-Operation-Id': bulkId };
    const seed: BulkResult<unknown>[] = targetSlugs.map((slug) => ({ slug, status: 'queued' }));
    setRun({ action, title, rows: seed, running: true });

    const results = await bulkOperation<TResult>(
      targetSlugs,
      (slug) => op(slug, headers),
      {
        concurrency: 5,
        onUpdate: (entry, idx) => {
          setRun((prev) => {
            if (!prev) return prev;
            const nextRows = prev.rows.slice();
            nextRows[idx] = entry as BulkResult<unknown>;
            return { ...prev, rows: nextRows };
          });
        },
      },
    );

    setRun((prev) =>
      prev ? { ...prev, rows: results as BulkResult<unknown>[], running: false } : prev,
    );
    refresh();
    // Clear selection on remove so the operator doesn't act on
    // stale slugs that no longer exist in the registry.
    if (action === 'remove') setSelected(new Set());
  }

  function bulkEnable(slugs: string[]): Promise<void> {
    return runBulk('enable', `Federation aktivieren (${slugs.length})`, slugs, (slug, headers) =>
      api.post(`/enterprise-targets/${slug}/enable`, {}, { headers }),
    );
  }
  function bulkDisable(slugs: string[]): Promise<void> {
    return runBulk('disable', `Federation deaktivieren (${slugs.length})`, slugs, (slug, headers) =>
      api.post(`/enterprise-targets/${slug}/disable`, {}, { headers }),
    );
  }
  function bulkRemove(slugs: string[]): Promise<void> {
    return runBulk('remove', `Federation entfernen (${slugs.length})`, slugs, (slug, headers) =>
      api.delete(`/enterprise-targets/${slug}`, { headers }),
    );
  }

  function onRemoveClick(): void {
    if (selectedSlugs.length === 0) return;
    if (selectedSlugs.length > TYPED_CONFIRM_THRESHOLD) {
      setConfirmRemove(true);
    } else {
      // Even at small N, removing federation is destructive
      // enough to warrant the native confirm dialog — symmetric
      // with the single-row delete.
      if (!window.confirm(`${selectedSlugs.length} Federation-Eintrag(e) entfernen?`)) return;
      void bulkRemove(selectedSlugs);
    }
  }

  function onRetryFailed(failedSlugs: string[]): void {
    if (!run) return;
    if (run.action === 'enable') void bulkEnable(failedSlugs);
    else if (run.action === 'disable') void bulkDisable(failedSlugs);
    else if (run.action === 'remove') void bulkRemove(failedSlugs);
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-xl font-semibold">Enterprise federation targets</h2>
          <p className="text-sm text-slate-500">
            Per-tenant scoped <code>platform-admin</code> instances the central
            UI federates reads from (PR #1044). Heartbeat probes every ~60s.
          </p>
        </div>
        <button onClick={() => setShowForm((s) => !s)} className="btn-primary">
          {showForm ? 'Schliessen' : 'Target hinzufuegen'}
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-800 rounded p-3 mb-4 text-sm">
          {error}
        </div>
      )}

      {showForm && (
        <UpsertForm
          onClose={() => setShowForm(false)}
          onSaved={() => { setShowForm(false); refresh(); }}
          onError={setError}
        />
      )}

      {selectedSlugs.length > 0 && (
        <div
          role="region"
          aria-label="Bulk-Aktionen"
          className="bg-slate-50 border rounded p-3 mb-3 flex items-center gap-3 flex-wrap"
        >
          <span className="text-sm font-medium">
            {selectedSlugs.length} ausgewählt
          </span>
          <button
            onClick={() => bulkEnable(selectedSlugs)}
            className="btn-primary"
            title="Federation für ausgewählte Targets aktivieren"
          >
            Aktivieren
          </button>
          <button
            onClick={() => bulkDisable(selectedSlugs)}
            className="px-3 py-1 rounded border"
            title="Federation für ausgewählte Targets deaktivieren"
          >
            Deaktivieren
          </button>
          <button
            onClick={onRemoveClick}
            className="btn-danger"
            title="Federation-Einträge entfernen (DELETE)"
          >
            Entfernen
          </button>
          <button
            onClick={() => setSelected(new Set())}
            className="text-sm text-slate-500 hover:underline ml-auto"
          >
            Auswahl zurücksetzen
          </button>
        </div>
      )}

      {loading ? <p className="text-slate-500">Lade…</p> : (
        <table className="w-full bg-white shadow rounded">
          <thead className="bg-slate-100 text-left text-sm uppercase tracking-wide">
            <tr>
              <th className="p-3 w-10">
                <input
                  type="checkbox"
                  aria-label="Alle auswählen"
                  checked={allSelected}
                  onChange={toggleAll}
                  disabled={rows.length === 0}
                />
              </th>
              <th className="p-3">Slug</th>
              <th className="p-3">Base-URL</th>
              <th className="p-3">Status</th>
              <th className="p-3">Last reachable</th>
              <th className="p-3">Enabled</th>
              <th className="p-3">Aktionen</th>
            </tr>
          </thead>
          <tbody className="text-sm">
            {rows.map((t) => (
              <tr key={t.slug} className="border-t hover:bg-slate-50">
                <td className="p-3">
                  <input
                    type="checkbox"
                    aria-label={`Target ${t.slug} auswählen`}
                    checked={selected.has(t.slug)}
                    onChange={() => toggleRow(t.slug)}
                  />
                </td>
                <td className="p-3 font-mono">{t.slug}</td>
                <td className="p-3 text-slate-600">
                  <a href={t.baseUrl} target="_blank" rel="noreferrer" className="hover:underline">
                    {t.baseUrl}
                  </a>
                </td>
                <td className="p-3">
                  <span className={`badge ${STATUS_COLORS[t.lastStatus] ?? STATUS_COLORS.unknown}`}>
                    {STATUS_LABELS[t.lastStatus] ?? t.lastStatus}
                  </span>
                </td>
                <td className="p-3 text-slate-500" title={t.lastReachableAt ?? 'never'}>
                  {relativeAge(t.lastReachableAt)}
                </td>
                <td className="p-3">
                  <label className="inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={t.enabled}
                      disabled={busySlug === t.slug}
                      onChange={() => toggleEnabled(t)}
                    />
                    <span className="ml-2 text-slate-600 text-xs">
                      {t.enabled ? 'enabled' : 'disabled'}
                    </span>
                  </label>
                </td>
                <td className="p-3">
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => setHistoryTarget(t)}
                      disabled={busySlug === t.slug}
                      className="text-slate-600 hover:underline disabled:text-slate-400 disabled:no-underline"
                      title="Status-Verlauf der letzten 24h"
                    >
                      History
                    </button>
                    <button
                      onClick={() => deleteTarget(t)}
                      disabled={busySlug === t.slug}
                      className="text-red-600 hover:underline disabled:text-slate-400 disabled:no-underline"
                    >
                      Entfernen
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr><td colSpan={7} className="p-6 text-center text-slate-500">
                Noch keine federated Enterprise-Tenants. Der
                tenant-provision Playbook (PR #1042) registriert
                neue Enterprise-Tenants automatisch.
              </td></tr>
            )}
          </tbody>
        </table>
      )}

      {confirmRemove && (
        <TypedConfirmModal
          title={`${selectedSlugs.length} Federation-Einträge entfernen?`}
          description="DELETE entfernt die Registry-Zeile und unterbricht zentrale Reads für diese Tenants. Nur bei Decommission / Reorganisation."
          confirmPhrase="REMOVE"
          confirmLabel="Entfernen"
          affectedSlugs={selectedSlugs}
          onCancel={() => setConfirmRemove(false)}
          onConfirm={() => {
            setConfirmRemove(false);
            void bulkRemove(selectedSlugs);
          }}
        />
      )}

      {run && (
        <BulkOperationsModal
          title={run.title}
          rows={run.rows}
          running={run.running}
          onClose={() => setRun(null)}
          onRetryFailed={onRetryFailed}
        />
      )}

      {/* Welle 4.6 H-6 — Authenticated peers (last 24h). The
          panel lives below the registry table so operators see
          "what's registered" first and "who's actually been
          authenticating" second. Collapsed by default to keep
          the page scan-friendly when nothing's drifting. */}
      <AuthenticatedPeersPanel />

      {/* Welle 4.6 TD-6 — per-target health-history drill-down.
          Opens from the History button on each row; renders a
          timeline + stats card + CSV export. */}
      {historyTarget && (
        <HealthHistoryModal
          target={historyTarget}
          onClose={() => setHistoryTarget(null)}
        />
      )}
    </div>
  );
}

// --------------------------------------------------------------
// Welle 4.6 H-6 — Authenticated peers panel
// --------------------------------------------------------------
//
// Calls the central aggregator `GET /api/federation/auth-log`
// (which fans out to every enabled scoped instance) and renders
// per-tenant cards with the CN tallies + a drill-down modal of
// recent events. The "unknown CN" warning surface flags any CN
// that authenticated against a scoped instance but isn't in
// that instance's own allowlist — the H-6 brief's headline
// signal (rogue cert detection).
//
// Why a separate panel and not extending the registry table:
// the table is wide already (slug, base URL, status, last
// reachable, enabled, actions). Adding two more columns
// (accepted/rejected, unknown-CN badge) would push it off-screen
// on a 1280px laptop. A separate collapsible panel keeps the
// scan-friendly default and lets operators drill in only when
// they want to.

function AuthenticatedPeersPanel() {
  const [expanded, setExpanded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<FederationAuthLogResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [drilldownSlug, setDrilldownSlug] = useState<string | null>(null);

  // Lazy-load: only fetch when the operator expands the panel.
  // The aggregator fans out across every enabled tenant; we
  // don't want every page view to trigger N federated calls
  // when the operator usually doesn't care.
  function load(): void {
    setLoading(true);
    setError(null);
    api.get<FederationAuthLogResponse>('/federation/auth-log')
      .then((r) => setData(r.data))
      .catch((err) => setError(
        (err as { response?: { data?: { error?: string } }; message: string })
          .response?.data?.error ?? (err as Error).message,
      ))
      .finally(() => setLoading(false));
  }

  function toggle(): void {
    if (!expanded) {
      setExpanded(true);
      if (!data) load();
    } else {
      setExpanded(false);
    }
  }

  const tenants = data?.tenants ?? [];
  const totalAccepted = tenants.reduce((acc, t) => acc + t.accepted, 0);
  const totalRejected = tenants.reduce((acc, t) => acc + t.rejected, 0);
  const totalUnknown = tenants.reduce((acc, t) => acc + t.unknownCNs.length, 0);

  const drillTenant = drilldownSlug
    ? tenants.find((t) => t.slug === drilldownSlug) ?? null
    : null;

  return (
    <div className="mt-8 bg-white shadow rounded">
      <button
        onClick={toggle}
        className="w-full px-4 py-3 flex items-center justify-between hover:bg-slate-50 rounded-t"
      >
        <div className="flex items-center gap-3">
          <span className="text-slate-500">{expanded ? '▾' : '▸'}</span>
          <h3 className="font-semibold">Authenticated peers (last 24h)</h3>
          {data && (
            <span className="text-xs text-slate-500">
              {totalAccepted} accepted / {totalRejected} rejected
              {totalUnknown > 0 && (
                <span className="ml-2 inline-block px-2 py-0.5 rounded bg-orange-100 text-orange-800">
                  {totalUnknown} unknown CN{totalUnknown === 1 ? '' : 's'}
                </span>
              )}
            </span>
          )}
        </div>
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); load(); }}
          className="text-xs text-slate-500 hover:underline"
          disabled={loading || !expanded}
        >
          {loading ? 'Lade…' : 'Aktualisieren'}
        </button>
      </button>

      {expanded && (
        <div className="border-t p-4">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-800 rounded p-3 mb-4 text-sm">
              {error}
            </div>
          )}
          {loading && !data && <p className="text-slate-500 text-sm">Lade Auth-Log…</p>}
          {!loading && tenants.length === 0 && data && (
            <p className="text-slate-500 text-sm">
              Keine federated Tenants aktiv oder keine Authentifizierungen in den
              letzten 24h.
            </p>
          )}
          {tenants.length > 0 && (
            <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
              {tenants.map((t) => (
                <PeerCard
                  key={t.slug}
                  card={t}
                  onDrillDown={() => setDrilldownSlug(t.slug)}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {drillTenant && (
        <PeerDrillDownModal
          card={drillTenant}
          onClose={() => setDrilldownSlug(null)}
        />
      )}
    </div>
  );
}

interface PeerCardProps {
  card: FederationAuthLogTenantCard;
  onDrillDown: () => void;
}

function PeerCard({ card, onDrillDown }: PeerCardProps) {
  const hasUnknown = card.unknownCNs.length > 0;
  const allowlistEmpty = card.allowedCNs.length === 0;
  // Filter out the empty-CN "probe traffic" bucket from the
  // user-facing list; operators are interested in real CNs +
  // their counts. The empty-CN events still contribute to the
  // accepted/rejected totals so the bucket isn't invisible.
  const realCNs = card.uniqueCNs.filter((cn) => cn !== '');

  return (
    <div
      className={`border rounded p-3 cursor-pointer hover:bg-slate-50 ${
        hasUnknown ? 'border-orange-300 bg-orange-50' : 'border-slate-200'
      }`}
      onClick={onDrillDown}
    >
      <div className="flex items-center justify-between mb-2">
        <div className="font-mono text-sm font-semibold">{card.slug}</div>
        <span className={`badge ${STATUS_COLORS[card.status] ?? STATUS_COLORS.unknown}`}>
          {STATUS_LABELS[card.status] ?? card.status}
        </span>
      </div>
      {card.status !== 'reachable' && card.error && (
        <p className="text-xs text-red-700 mb-2">{card.error}</p>
      )}
      <div className="text-xs text-slate-600 space-y-1">
        <div>
          <span className="inline-block px-2 py-0.5 rounded bg-green-100 text-green-800">
            Accepted: {card.accepted}
          </span>
          {' '}
          <span className={`inline-block px-2 py-0.5 rounded ${
            card.rejected > 0 ? 'bg-red-100 text-red-800' : 'bg-slate-100 text-slate-600'
          }`}>
            Rejected: {card.rejected}
          </span>
        </div>
        <div>
          <span className="text-slate-500">Unique CNs:</span> {realCNs.length}
          {realCNs.length > 0 && (
            <span className="text-slate-500"> ({realCNs.slice(0, 3).join(', ')}{realCNs.length > 3 ? '…' : ''})</span>
          )}
        </div>
        {hasUnknown && (
          <div className="inline-block px-2 py-0.5 rounded bg-orange-200 text-orange-900 font-semibold">
            ⚠ Unknown CN: {card.unknownCNs.join(', ')}
          </div>
        )}
        {allowlistEmpty && (
          <div className="inline-block px-2 py-0.5 rounded bg-amber-100 text-amber-900">
            ⚠ Allowlist leer (Gate disabled)
          </div>
        )}
      </div>
    </div>
  );
}

interface PeerDrillDownModalProps {
  card: FederationAuthLogTenantCard;
  onClose: () => void;
}

function PeerDrillDownModal({ card, onClose }: PeerDrillDownModalProps) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40"
      onClick={onClose}
    >
      <div
        className="bg-white rounded shadow-lg max-w-3xl w-full max-h-[80vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <div>
            <h3 className="font-semibold">Auth-Log: <span className="font-mono">{card.slug}</span></h3>
            <p className="text-xs text-slate-500">
              Allowlist: {card.allowedCNs.length === 0 ? 'leer (Gate disabled)' : card.allowedCNs.join(', ')}
            </p>
          </div>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-700 text-xl px-2">×</button>
        </div>
        <div className="overflow-auto p-4">
          {card.events.length === 0 ? (
            <p className="text-slate-500 text-sm">Keine Auth-Events in diesem Fenster.</p>
          ) : (
            <table className="w-full text-xs">
              <thead className="text-left bg-slate-100">
                <tr>
                  <th className="p-2">Zeit</th>
                  <th className="p-2">CN</th>
                  <th className="p-2">Result</th>
                  <th className="p-2">Pfad</th>
                  <th className="p-2">Grund</th>
                </tr>
              </thead>
              <tbody>
                {card.events.map((ev) => (
                  <EventRow key={ev.id} ev={ev} allowedCNs={card.allowedCNs} />
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

interface EventRowProps {
  ev: FederationAuthEvent;
  allowedCNs: string[];
}

function EventRow({ ev, allowedCNs }: EventRowProps) {
  const isUnknown =
    allowedCNs.length > 0 && ev.cn !== '' && !allowedCNs.includes(ev.cn);
  return (
    <tr className={`border-t ${isUnknown ? 'bg-orange-50' : ''}`}>
      <td className="p-2 font-mono text-slate-600" title={ev.at}>
        {new Date(ev.at).toLocaleString()}
      </td>
      <td className="p-2 font-mono">
        {ev.cn || <span className="text-slate-400 italic">(no CN)</span>}
        {isUnknown && <span className="ml-1 text-orange-700">⚠</span>}
      </td>
      <td className="p-2">
        <span className={`badge ${
          ev.result === 'accepted'
            ? 'bg-green-100 text-green-800'
            : 'bg-red-100 text-red-800'
        }`}>
          {ev.result}
        </span>
      </td>
      <td className="p-2 font-mono text-slate-600">{ev.method} {ev.path}</td>
      <td className="p-2 text-slate-500">{ev.reason ?? '—'}</td>
    </tr>
  );
}

interface UpsertFormProps {
  onClose: () => void;
  onSaved: () => void;
  onError: (msg: string) => void;
}

function UpsertForm({ onClose, onSaved, onError }: UpsertFormProps) {
  const { domain } = useBrand();
  const [slug, setSlug] = useState('');
  const [baseUrl, setBaseUrl] = useState('');
  const [hmacSecretOpenBaoPath, setPath] = useState('');
  const [busy, setBusy] = useState(false);

  // When the user types the slug we auto-derive the conventional
  // baseUrl + OpenBao path. Both are still editable — the
  // playbook follows the same convention, so manual edits are
  // rare (recovery scenarios after a tenant restore, custom
  // hostnames).
  function applySlug(s: string): void {
    const next = s.toLowerCase().replace(/[^a-z0-9-]/g, '');
    setSlug(next);
    if (next) {
      setBaseUrl(`https://admin-${next}.${domain}`);
      setPath(`sectepe/platform-admin/federated/${next}/hmac`);
    }
  }

  function isValidSlug(): boolean {
    return SLUG_RE.test(slug) && !RESERVED.has(slug);
  }

  async function submit(e: FormEvent): Promise<void> {
    e.preventDefault();
    if (!isValidSlug()) {
      onError('Slug ist nicht DNS-konform oder ein reserviertes Wort.');
      return;
    }
    if (!baseUrl.startsWith('https://')) {
      onError('Base-URL muss mit https:// beginnen.');
      return;
    }
    setBusy(true);
    try {
      await api.put('/enterprise-targets', {
        slug,
        baseUrl,
        hmacSecretOpenBaoPath,
        enabled: true,
      });
      onSaved();
    } catch (err) {
      onError((err as { response?: { data?: { error?: string } }; message: string })
        .response?.data?.error ?? (err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={submit} className="bg-white shadow rounded p-4 mb-4 space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold">Target hinzufuegen / aktualisieren</h3>
        <button type="button" onClick={onClose} className="text-slate-500 hover:text-slate-700">
          ×
        </button>
      </div>
      <p className="text-xs text-slate-500">
        Idempotent: PUT mit gleichem Slug aktualisiert den vorhandenen
        Eintrag (z.B. nach OpenBao-Pfad-Migration).
      </p>
      <div>
        <label className="block text-sm font-medium mb-1">Tenant-Slug</label>
        <input
          value={slug}
          onChange={(e) => applySlug(e.target.value)}
          placeholder="z.B. acme"
          className="w-full px-2 py-1 rounded border font-mono text-sm"
          required
        />
      </div>
      <div>
        <label className="block text-sm font-medium mb-1">Base-URL</label>
        <input
          value={baseUrl}
          onChange={(e) => setBaseUrl(e.target.value)}
          placeholder={`https://admin-acme.${domain}`}
          className="w-full px-2 py-1 rounded border text-sm"
          required
        />
      </div>
      <div>
        <label className="block text-sm font-medium mb-1">HMAC-Secret OpenBao-Pfad</label>
        <input
          value={hmacSecretOpenBaoPath}
          onChange={(e) => setPath(e.target.value)}
          placeholder="sectepe/platform-admin/federated/acme/hmac"
          className="w-full px-2 py-1 rounded border font-mono text-sm"
          required
        />
        <p className="text-xs text-slate-500 mt-1">
          Erwarteter Schluessel im Pfad: <code>HMAC_SECRET</code> (siehe enterprise-target.service.ts).
        </p>
      </div>
      <div className="flex gap-2 justify-end">
        <button type="button" onClick={onClose} className="px-3 py-1 rounded border" disabled={busy}>
          Abbrechen
        </button>
        <button type="submit" className="btn-primary" disabled={busy || !isValidSlug()}>
          {busy ? 'Speichere…' : 'Speichern'}
        </button>
      </div>
    </form>
  );
}

// --------------------------------------------------------------
// Welle 4.6 TD-6 — per-target health-history drill-down modal
// --------------------------------------------------------------
//
// Fetches /api/enterprise-targets/:slug/history on open and
// renders:
//   - Header: slug + current status badge + last-reachable age
//   - Stats card: 24h uptime ratio, avg duration, breaker opens
//   - Timeline: recent events with relative timestamps,
//     status badges, durations, collapsible errors
//
// Performance note (per the brief): we cap server-side at 200
// events. We render the full slice (no virtualisation) because
// 200 rows is well inside what React can paint per frame. A
// truly high-flap tenant exceeding 200 events in 24h would
// surface that via the "showing X of Y" hint computed off
// `total`; pagination is a follow-up.
//
// We intentionally do NOT auto-refresh: an operator triaging a
// past flap event wants the snapshot they opened with. A
// manual refresh button reloads on demand.

interface HealthHistoryModalProps {
  target: EnterpriseTarget;
  onClose: () => void;
}

function HealthHistoryModal({ target, onClose }: HealthHistoryModalProps) {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<EnterpriseTargetHistoryResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [expandedErrors, setExpandedErrors] = useState<Set<string>>(new Set());

  function load(): void {
    setLoading(true);
    setError(null);
    api.get<EnterpriseTargetHistoryResponse>(`/enterprise-targets/${target.slug}/history`)
      .then((r) => setData(r.data))
      .catch((err) => setError(
        (err as { response?: { data?: { error?: string } }; message: string })
          .response?.data?.error ?? (err as Error).message,
      ))
      .finally(() => setLoading(false));
  }

  useEffect(() => {
    load();
    // Re-run when the target slug changes (operator switches
    // history between rows without closing the modal in between
    // — exhaustive-deps would otherwise warn, and we intentionally
    // depend on `target.slug` only because `target` itself is a
    // new object on every render from the parent).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target.slug]);

  const events = data?.rows ?? [];
  const stats = useMemo(() => computeHistoryStats(events), [events]);
  const truncated = data ? data.total > events.length : false;

  function toggleErrorExpansion(id: string): void {
    setExpandedErrors((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  // The CSV export goes through the dot-suffixed sibling path so
  // the browser's download dialog defaults to a sensible filename
  // (same QoL pattern as /api/audit.csv). We build the href
  // off the same base path the JSON endpoint uses; the operator
  // bearer token is attached by the axios interceptor for the
  // JSON read, but a plain anchor download won't carry the
  // Authorization header — for the MVP we let the browser hit
  // the URL directly with the existing cookie-less OIDC flow.
  // The endpoint accepts a `token` query param for this case in
  // a follow-up; for now CSV is best-used via curl.
  const csvHref = `/api/enterprise-targets/${target.slug}/history.csv`;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40"
      onClick={onClose}
    >
      <div
        className="bg-white rounded shadow-lg max-w-4xl w-full max-h-[85vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-label={`Federation-History fuer ${target.slug}`}
      >
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <div>
            <h3 className="font-semibold">
              Federation-History: <span className="font-mono">{target.slug}</span>
            </h3>
            <p className="text-xs text-slate-500 mt-1 flex items-center gap-2">
              <span>aktueller Status:</span>
              <span
                className={`badge ${STATUS_COLORS[target.lastStatus] ?? STATUS_COLORS.unknown}`}
              >
                {STATUS_LABELS[target.lastStatus] ?? target.lastStatus}
              </span>
              <span className="text-slate-400">·</span>
              <span title={target.lastReachableAt ?? 'never'}>
                last reachable {relativeAge(target.lastReachableAt)}
              </span>
            </p>
          </div>
          <div className="flex items-center gap-3">
            <a
              href={csvHref}
              className="text-sm text-slate-600 hover:underline"
              title="Status-Verlauf als CSV exportieren"
            >
              Export CSV
            </a>
            <button
              onClick={load}
              className="text-sm text-slate-500 hover:underline"
              disabled={loading}
              title="Verlauf neu laden"
            >
              {loading ? 'Lade…' : 'Aktualisieren'}
            </button>
            <button
              onClick={onClose}
              className="text-slate-500 hover:text-slate-700 text-xl px-2"
              aria-label="Schliessen"
            >
              ×
            </button>
          </div>
        </div>

        <div className="overflow-auto p-4 space-y-4">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-800 rounded p-3 text-sm">
              {error}
            </div>
          )}

          {!error && (
            <HistoryStatsCard
              stats={stats}
              truncated={truncated}
              total={data?.total ?? 0}
            />
          )}

          {!error && loading && !data && (
            <p className="text-slate-500 text-sm">Lade Verlauf…</p>
          )}

          {!error && !loading && events.length === 0 && (
            <p className="text-slate-500 text-sm" data-testid="history-empty-state">
              Keine Status-Wechsel in den letzten 24h. Dieser Tenant
              flippt nicht — die Federation-Verbindung ist seit dem
              letzten Heartbeat-Tick stabil.
            </p>
          )}

          {events.length > 0 && (
            <ul className="divide-y border rounded">
              {events.map((ev) => (
                <HistoryEventRow
                  key={ev.id}
                  ev={ev}
                  expanded={expandedErrors.has(ev.id)}
                  onToggle={() => toggleErrorExpansion(ev.id)}
                />
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

interface HistoryStatsCardProps {
  stats: ReturnType<typeof computeHistoryStats>;
  truncated: boolean;
  total: number;
}

function HistoryStatsCard({ stats, truncated, total }: HistoryStatsCardProps) {
  const uptime = stats.uptimeRatio === null
    ? '—'
    : `${(stats.uptimeRatio * 100).toFixed(1)}%`;
  const avgDuration = stats.avgDurationMs === null
    ? '—'
    : `${Math.round(stats.avgDurationMs)} ms`;
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
      <div className="border rounded p-3">
        <div className="text-xs text-slate-500 uppercase tracking-wide">
          Uptime (24h)
        </div>
        <div className="text-lg font-semibold">{uptime}</div>
        <div className="text-xs text-slate-500 mt-1">
          {stats.reachableCount}/{stats.total} Events reachable
        </div>
      </div>
      <div className="border rounded p-3">
        <div className="text-xs text-slate-500 uppercase tracking-wide">
          ⌀ Request-Dauer
        </div>
        <div className="text-lg font-semibold">{avgDuration}</div>
        <div className="text-xs text-slate-500 mt-1">
          Heartbeat + operator reads
        </div>
      </div>
      <div className="border rounded p-3">
        <div className="text-xs text-slate-500 uppercase tracking-wide">
          Breaker opens
        </div>
        <div className="text-lg font-semibold">{stats.breakerOpenCount}</div>
        <div className="text-xs text-slate-500 mt-1">
          Wechsel nach <code>unreachable</code>
        </div>
      </div>
      {truncated && (
        <div className="md:col-span-3 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
          ⚠ {stats.total} von {total} Events angezeigt (Server-Cap
          200). Engere Zeitspanne via <code>?since=…</code> wählen.
        </div>
      )}
    </div>
  );
}

interface HistoryEventRowProps {
  ev: EnterpriseTargetHistoryEvent;
  expanded: boolean;
  onToggle: () => void;
}

function HistoryEventRow({ ev, expanded, onToggle }: HistoryEventRowProps) {
  const hasError = typeof ev.error === 'string' && ev.error.length > 0;
  return (
    <li className="p-3 text-sm flex items-start gap-3">
      <div className="flex-shrink-0 w-36">
        <div
          className="font-mono text-xs text-slate-500"
          title={ev.at}
        >
          {relativeAge(ev.at)}
        </div>
        <div className="text-[10px] text-slate-400 font-mono">
          {new Date(ev.at).toLocaleString()}
        </div>
      </div>
      <div className="flex-1">
        <div className="flex items-center gap-2">
          <span
            className={`badge ${STATUS_COLORS[ev.status] ?? STATUS_COLORS.unknown}`}
          >
            {STATUS_LABELS[ev.status] ?? ev.status}
          </span>
          {typeof ev.durationMs === 'number' && (
            <span className="text-xs text-slate-500">
              {ev.durationMs} ms
            </span>
          )}
          {hasError && (
            <button
              onClick={onToggle}
              className="text-xs text-slate-500 hover:underline"
            >
              {expanded ? 'Fehler ausblenden' : 'Fehler anzeigen'}
            </button>
          )}
        </div>
        {hasError && expanded && (
          <pre className="mt-2 text-xs bg-red-50 border border-red-200 rounded p-2 whitespace-pre-wrap break-all">
            {ev.error}
          </pre>
        )}
      </div>
    </li>
  );
}
